import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Category, CartItem } from '../types';
import { productsAPI, ordersAPI, settingsAPI, giftCodesAPI, loyaltyCodesAPI, realtimeCartAPI, payconiqAPI } from 'services/api';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button, Modal } from './ui';
import { printStandardReceipt } from '../utils/printReceipt';
import { secureGetItem } from '../utils/secureStorage';
import { getCurrentTimeBelgiumString } from '../utils/dateUtils';

interface Product {
  id: number;
  name: string;
  price: number;
  tax_rate: number;
  category_id: number;
  image_url?: string | null;
  stock_quantity?: number | null;
  is_active: boolean;
  created_at: string;
  category?: Category;
}

const QuickOrderDeneme: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageTitle] = useState('🎉 Gold Cafe T');
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState<'nakit' | 'kart' | 'qr'>('nakit');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [qrPaymentData, setQrPaymentData] = useState<any>(null);
  const [qrPaymentStatus, setQrPaymentStatus] = useState<string>('');
  const [isQrPaymentActive, setIsQrPaymentActive] = useState(false);
  const [qrPaymentInterval, setQrPaymentInterval] = useState<NodeJS.Timeout | null>(null);
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);
  const [qrPaymentCancelledByUser, setQrPaymentCancelledByUser] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<any>(null);
  const [completedOrderGiftCode, setCompletedOrderGiftCode] = useState<any>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productNote, setProductNote] = useState('');
  const [wantsReceipt, setWantsReceipt] = useState(false);
  const [giftCode, setGiftCode] = useState('');
  const [appliedGiftCode, setAppliedGiftCode] = useState<any>(null);
  const [giftCodeDiscount, setGiftCodeDiscount] = useState(0);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [loyaltyCode, setLoyaltyCode] = useState('');
  const [appliedLoyaltyCode, setAppliedLoyaltyCode] = useState<any>(null);
  const [tempLoyaltyCount, setTempLoyaltyCount] = useState(0);
  const [isValidatingLoyaltyCode, setIsValidatingLoyaltyCode] = useState(false);
  const [pendingLoyaltyReward, setPendingLoyaltyReward] = useState<any>(null);
  const [receiptSettings, setReceiptSettings] = useState({
    receiptTitle: '',
    receiptSubtitle: '',
    receiptPhone: '',
    receiptTaxNumber: '',
    receiptFooter: '',
    receiptDateLabel: '',
    receiptTimeLabel: '',
    receiptOrderNumberLabel: '',
    receiptOrderDetailsLabel: '',
    receiptPaymentLabel: '',
    receiptPaymentCash: '',
    receiptPaymentCard: '',
    receiptSubtotalLabel: '',
    receiptTaxLabel: '',
    receiptTotalLabel: '',
    receiptQuantityLabel: '',
    receiptShowTaxDetails: false,
    receiptShowPaymentType: false,
    receiptShowOrderNumber: false,
    receiptShowDateTime: false
  });

  const { user, logout: originalLogout } = useAuth();
  const { formatPrice } = useCurrency();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const catScrollRef = useRef<HTMLDivElement | null>(null);
  const [catCanScrollLeft, setCatCanScrollLeft] = useState(false);
  const [catCanScrollRight, setCatCanScrollRight] = useState(false);

  const logout = async () => {
    setCart([]);
    setAppliedGiftCode(null);
    setGiftCode('');
    setAppliedLoyaltyCode(null);
    setLoyaltyCode('');
    setTempLoyaltyCount(0);
    setPendingLoyaltyReward(null);
    originalLogout();
  };

  useEffect(() => {
    return () => {
      const clearCartOnUnmount = async () => {
        try {
          const token = secureGetItem('token');
          if (token) {
            const csrfResponse = await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/csrf-token`, {
              credentials: 'include'
            });
            const csrfData = await csrfResponse.json();
            await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/realtime-cart/clear`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-CSRF-Token': csrfData.csrfToken
              },
              credentials: 'include'
            });
          }
        } catch (error) {
        }
      };
      clearCartOnUnmount();
      if (qrPaymentInterval) {
        clearInterval(qrPaymentInterval);
      }
    };
  }, [qrPaymentInterval]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const clearCartOnPageLeave = async () => {
        try {
          const token = secureGetItem('token');
          if (token) {
            await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/realtime-cart/clear`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            });
          }
        } catch (error) {
        }
      };
      clearCartOnPageLeave();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const calculateTotal = useCallback(() => {
    return cart.reduce((total, item) => {
      if (item.notes === '🎁 ÖDÜL') {
        return total;
      }
      return total + (item.product.price * item.quantity);
    }, 0);
  }, [cart]);

  const calculateTax = () => {
    return cart.reduce((total, item) => {
      if (item.notes === '🎁 ÖDÜL') {
        return total;
      }
      const itemTotal = item.product.price * item.quantity;
      const taxRate = item.product.tax_rate || 0;
      const taxAmount = itemTotal - (itemTotal / (1 + taxRate / 100));
      return total + taxAmount;
    }, 0);
  };

  const calculateGrandTotal = () => {
    return calculateTotal();
  };

  const validateGiftCode = async () => {
    if (!giftCode.trim()) {
      toast.error('Lütfen bir hediye kodu girin');
      return;
    }
    setIsValidatingCode(true);
    try {
      const cartTotal = calculateGrandTotal();
      const cartItems = cart.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity,
        unit_price: item.product.price
      }));
      const validation = await giftCodesAPI.validate(giftCode.trim(), cartTotal, cartItems);
      if (validation.valid) {
        setAppliedGiftCode(validation.gift_code);
        setGiftCodeDiscount(validation.discount_amount);
        toast.success(`✅ ${validation.message}`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Kod doğrulanamadı');
      setAppliedGiftCode(null);
      setGiftCodeDiscount(0);
    } finally {
      setIsValidatingCode(false);
    }
  };

  const validateLoyaltyCode = async () => {
    if (!loyaltyCode.trim()) {
      toast.error('Lütfen bir sadakat kodu girin');
      return;
    }
    setIsValidatingLoyaltyCode(true);
    try {
      const foundCode = await loyaltyCodesAPI.validate(loyaltyCode.trim());
      if (foundCode.reward_used_at) {
        toast.error('❌ Bu ödül zaten kullanılmış');
        setAppliedLoyaltyCode(null);
        return;
      }
      if (foundCode.is_completed && !foundCode.reward_used_at) {
        setAppliedLoyaltyCode(foundCode);
        toast.success(`🎉 Sadakat ödülü kullanılabilir! ${foundCode.reward_product_name}`);
      } else if (foundCode.is_completed && foundCode.reward_used_at) {
        toast.error('❌ Bu ödül zaten kullanılmış');
        setAppliedLoyaltyCode(null);
      } else {
        const targetProductQuantity = cart.reduce((total, item) => {
          return item.product.id === foundCode.target_product_id 
            ? total + item.quantity 
            : total;
        }, 0);
        const tempCount = targetProductQuantity > 0 ? targetProductQuantity : 1;
        const newTotalCount = foundCode.current_count + tempCount;
        setAppliedLoyaltyCode(foundCode);
        setTempLoyaltyCount(tempCount);
        if (newTotalCount >= foundCode.required_quantity) {
          toast.success(`🎉 Sadakat kodu tamamlanacak: ${newTotalCount}/${foundCode.required_quantity} - Sipariş tamamlandığında ödül kazanılacak!`);
        } else {
          toast.success(`📊 Sadakat kodu eklendi: ${newTotalCount}/${foundCode.required_quantity} - Sipariş tamamlandığında sayım artacak`);
        }
      }
    } catch (error: any) {
      toast.error(`Kod doğrulanamadı: ${error.message || 'Bilinmeyen hata'}`);
      setAppliedLoyaltyCode(null);
    } finally {
      setIsValidatingLoyaltyCode(false);
    }
  };

  const removeLoyaltyCode = async () => {
    setCart(prevCart => prevCart.filter(item => item.notes !== '🎁 ÖDÜL'));
    setAppliedLoyaltyCode(null);
    setTempLoyaltyCount(0);
    setLoyaltyCode('');
    setPendingLoyaltyReward(null);
    try {
      const payload = {
        items: cart.map(item => ({
          id: item.product.id,
          product_name: item.product.name,
          quantity: item.quantity,
          unit_price: item.product.price,
          notes: item.notes,
          is_reward: item.notes === '🎁 ÖDÜL'
        })),
        totals: {
          subtotal: cart.reduce((total, item) => item.notes === '🎁 ÖDÜL' ? total : total + (item.product.price * item.quantity), 0),
          tax: cart.reduce((total, item) => {
            if (item.notes === '🎁 ÖDÜL') return total;
            const itemSubtotal = item.product.price * item.quantity;
            const taxRate = item.product.tax_rate || 0;
            return total + (itemSubtotal * (taxRate / 100));
          }, 0),
          total: cart.reduce((total, item) => {
            if (item.notes === '🎁 ÖDÜL') return total;
            const itemSubtotal = item.product.price * item.quantity;
            const taxRate = item.product.tax_rate || 0;
            return total + itemSubtotal + (itemSubtotal * (taxRate / 100));
          }, 0) - giftCodeDiscount,
          discount: giftCodeDiscount
        },
        enabled: true,
        coupons: {
          giftCode: appliedGiftCode ? {
            code: appliedGiftCode.code,
            discount: appliedGiftCode.discount || 0,
            type: appliedGiftCode.type
          } : null,
          loyaltyCode: null
        },
        preserveQrPayment: true
      };
      await realtimeCartAPI.update(payload);
    } catch (error) {
    }
    toast.success('✅ Sadakat kodu kaldırıldı');
  };

  const applyLoyaltyReward = async () => {
    if (!appliedLoyaltyCode) {
      toast.error('Kullanılabilir ödül yok');
      return;
    }
    const isCompleted = appliedLoyaltyCode.is_completed || 
      (tempLoyaltyCount > 0 && (appliedLoyaltyCode.current_count + tempLoyaltyCount) >= appliedLoyaltyCode.required_quantity);
    if (!isCompleted || appliedLoyaltyCode.reward_used_at || pendingLoyaltyReward) {
      toast.error('Kullanılabilir ödül bulunamadı veya ödül zaten kullanılmış/beklemede');
      return;
    }

    try {
      const rewardProduct = products.find(p => p.id === appliedLoyaltyCode.reward_product_id) ||
        products.find(p => p.name === appliedLoyaltyCode.reward_product_name);

      if (!rewardProduct) {
        toast.error('❌ Ödül ürünü bulunamadı!');
        return;
      }

      setCart(prevCart => {
        const existingItem = prevCart.find(item => 
          item.product.id === rewardProduct.id && 
          item.notes === '🎁 ÖDÜL'
        );
        if (existingItem) {
          return prevCart.map(item =>
            item.product.id === rewardProduct.id && item.notes === '🎁 ÖDÜL'
              ? { ...item, quantity: item.quantity + 1 }
              : item
          );
        } else {
          return [...prevCart, { product: rewardProduct, quantity: 1, notes: '🎁 ÖDÜL' }];
        }
      });

      toast.success(`🎉 ${appliedLoyaltyCode.reward_product_name} ücretsiz eklendi! Sepeti tamamladığınızda ödül kullanılacak.`);

      setPendingLoyaltyReward({
        loyaltyCodeId: appliedLoyaltyCode.id,
        productId: appliedLoyaltyCode.reward_product_id,
        tempLoyaltyCount: tempLoyaltyCount
      });
    } catch (error) {
      toast.error('❌ Ödül sepete eklenemedi');
    }
  };

  const openNoteModal = (product: Product) => {
    setSelectedProduct(product);
    setProductNote('');
    setShowNoteModal(true);
  };

  const addToCartWithNote = () => {
    if (selectedProduct) {
      addToCart(selectedProduct, productNote);
      setShowNoteModal(false);
      setSelectedProduct(null);
      setProductNote('');
    }
  };

  const addToCart = (product: Product, notes?: string) => {
    if (product.stock_quantity !== null && product.stock_quantity !== undefined) {
      const existingItem = cart.find(item => 
        item.product.id === product.id && item.notes === (notes || '')
      );
      const currentQuantity = existingItem ? existingItem.quantity : 0;
      if (product.stock_quantity <= 0 || currentQuantity >= product.stock_quantity) {
        toast.error(`${product.name} için yeterli stok yok!`);
        return;
      }
    }
    setCart(prevCart => {
      const existingItem = prevCart.find(item => 
        item.product.id === product.id && 
        item.notes === (notes || '')
      );
      let newCart: typeof prevCart;
      if (existingItem) {
        newCart = prevCart.map(item =>
          item.product.id === product.id && item.notes === (notes || '')
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        newCart = [...prevCart, { product, quantity: 1, notes: notes || '' }];
      }
      return newCart;
    });
    if (appliedLoyaltyCode && !appliedLoyaltyCode.is_completed) {
      if (appliedLoyaltyCode.target_product_id === product.id) {
        const newCount = (appliedLoyaltyCode.current_count || 0) + 1;
        const isCompleted = newCount >= appliedLoyaltyCode.required_quantity;
        setAppliedLoyaltyCode({
          ...appliedLoyaltyCode,
          current_count: newCount,
          is_completed: isCompleted,
          reward_earned_at: isCompleted ? new Date().toISOString() : appliedLoyaltyCode.reward_earned_at
        });
        toast.success(isCompleted ? `🎉 Sadakat kodu tamamlandı! Ödül kazanıldı: ${appliedLoyaltyCode.reward_product_name}` : `📊 Sadakat kodu güncellendi: ${newCount}/${appliedLoyaltyCode.required_quantity}`);
      }
    }
    toast.success(`${product.name} sepete eklendi`);
  };

  const updateCartItemQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(index);
      return;
    }
    setCart(prevCart => {
      const newCart = prevCart.map((item, i) => i === index ? { ...item, quantity } : item);
      setTimeout(() => { updateRealtimeCartWithData(newCart); }, 0);
      return newCart;
    });
  };

  const removeFromCart = (index: number) => {
    setCart(prevCart => {
      const newCart = prevCart.filter((_, i) => i !== index);
      setTimeout(() => { updateRealtimeCartWithData(newCart); }, 0);
      return newCart;
    });
  };

  const clearCart = async (preserveQrPayment = true) => {
    setCart([]);
    setAppliedLoyaltyCode(null);
    setLoyaltyCode('');
    setTempLoyaltyCount(0);
    setPendingLoyaltyReward(null);
    try {
      await updateRealtimeCartWithData([], true, preserveQrPayment);
    } catch (error) {}
  };

  const startPaymentProcess = () => {
    if (cart.length === 0) {
      toast.error('Sepet boş!');
      return;
    }
    setIsProcessingPayment(false);
    setShowPaymentModal(true);
  };

  const processQuickPayment = async () => {
    setIsProcessingPayment(false);
    setIsQrPaymentActive(false);
    setQrPaymentData(null);
    setQrPaymentStatus('');
    setCurrentOrderId(null);
    if (qrPaymentInterval) {
      clearInterval(qrPaymentInterval);
      setQrPaymentInterval(null);
    }
    await new Promise(resolve => setTimeout(resolve, 100));
    try {
      setIsProcessingPayment(true);
      await new Promise(resolve => setTimeout(resolve, 100));
      if (!user?.id) {
        toast.error('Kullanıcı bilgisi bulunamadı');
        return;
      }
      const loyaltyQuantity = appliedLoyaltyCode ? cart.reduce((total, item) => item.product.id === appliedLoyaltyCode.target_product_id ? total + item.quantity : total, 0) : 0;
      const orderData = {
        table_id: 0,
        user_id: user.id,
        receipt_requested: wantsReceipt,
        loyalty_code_id: appliedLoyaltyCode?.id || null,
        loyalty_quantity: loyaltyQuantity,
        items: cart.map(item => ({ product_id: item.product.id, quantity: item.quantity, unit_price: item.product.price, notes: item.notes || '' }))
      };
      let newOrder: any;
      newOrder = await ordersAPI.create(orderData);
      const subtotal = cart.reduce((total, item) => item.notes === '🎁 ÖDÜL' ? total : total + (item.product.price * item.quantity), 0);
      const totalAmount = subtotal - giftCodeDiscount;
      if (appliedGiftCode && giftCodeDiscount > 0) {
        try {
          await giftCodesAPI.use(appliedGiftCode.id, newOrder.id, user?.username || 'Bilinmeyen', giftCodeDiscount);
          toast.success(`🎁 Hediye kodu kullanıldı: -${formatPrice(giftCodeDiscount)}`);
        } catch (error) {
          toast.error('Hediye kodu kaydedilemedi, ancak sipariş oluşturuldu');
        }
      }
      if (paymentType === 'qr') {
        try {
          toast.loading('QR ödeme hazırlanıyor...', { id: 'qr-loading' });
          try { await payconiqAPI.testService(); } catch (testError) {
            toast.dismiss('qr-loading');
            toast.error('Payconiq servisi çalışmıyor. Lütfen backend\'i kontrol edin.');
            setIsProcessingPayment(false);
            return;
          }
          const qrData = await payconiqAPI.initiatePayment(newOrder.id, totalAmount, `Hızlı Sipariş #${newOrder.id}`);
          setQrPaymentData(qrData);
          setQrPaymentStatus('PENDING');
          setIsQrPaymentActive(true);
          setCurrentOrderId(newOrder.id);
          setQrPaymentCancelledByUser(false);
          try {
            await realtimeCartAPI.sendQRPayment({
              orderId: newOrder.id,
              qrCodeUrl: qrData.qrCodeUrl,
              amount: totalAmount,
              description: `Hızlı Sipariş #${newOrder.id}`,
              expiresAt: qrData.expiresAt,
              paymentId: qrData.paymentId,
              deepLinkUrl: qrData.deepLinkUrl
            });
          } catch (error) {}
          toast.dismiss('qr-loading');
          toast.success('📱 QR ödeme başlatıldı! Müşteri ekranında QR kodu görünüyor.');
          setIsProcessingPayment(false);
          startQrPaymentStatusCheck(newOrder.id);
          return;
        } catch (error: any) {
          toast.dismiss('qr-loading');
          toast.error(`❌ QR ödeme başlatılamadı: ${error?.message || error?.response?.data?.error || 'Bilinmeyen hata'}`);
          setIsProcessingPayment(false);
          return;
        }
      }
      await ordersAPI.pay(newOrder.id, paymentType);
      if (pendingLoyaltyReward) {
        try {
          await loyaltyCodesAPI.useReward(pendingLoyaltyReward.loyaltyCodeId, pendingLoyaltyReward.productId, pendingLoyaltyReward.tempLoyaltyCount);
          toast.success(`🎁 Ödül kullanıldı!`);
          if (appliedLoyaltyCode) {
            setAppliedLoyaltyCode({ ...appliedLoyaltyCode, reward_used_at: new Date().toISOString() });
          }
          setPendingLoyaltyReward(null);
        } catch (error) {
          toast.error('Ödül kullanımı kaydedilemedi, ancak ödeme tamamlandı');
        }
      }
      toast.success('Ödeme alındı! Sipariş mutfağa gönderildi ve stok güncellendi.');
      const completedOrderData = await ordersAPI.getById(newOrder.id);
      setCompletedOrder({ ...completedOrderData, payment_type: paymentType, completed_at: new Date().toISOString() });
      setShowPaymentModal(false);
      setShowReceiptModal(true);
      await clearCart();
      setGiftCode('');
      setAppliedGiftCode(null);
      setGiftCodeDiscount(0);
      setLoyaltyCode('');
      setAppliedLoyaltyCode(null);
      setTempLoyaltyCount(0);
      setPendingLoyaltyReward(null);
      try {
        const allProductsData = await productsAPI.getAll();
        setAllProducts(allProductsData);
        if (selectedCategory !== null) {
          const filteredProducts = allProductsData.filter(product => product.category_id === selectedCategory && product.is_active);
          setProducts(filteredProducts);
        } else {
          const activeProducts = allProductsData.filter(product => product.is_active);
          setProducts(activeProducts);
        }
      } catch (error) {}
    } catch (error: any) {
      toast.error('Ödeme işlenirken hata oluştu');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const startQrPaymentStatusCheck = (orderId: number) => {
    if (qrPaymentInterval) {
      clearInterval(qrPaymentInterval);
    }
    const checkInterval = setInterval(async () => {
      try {
        const status = await payconiqAPI.getPaymentStatus(orderId);
        setQrPaymentStatus(status.status);
        if (status.status === 'SUCCEEDED') {
          clearInterval(checkInterval);
          setQrPaymentInterval(null);
          setIsQrPaymentActive(false);
          setQrPaymentData(null);
          setCurrentOrderId(null);
          setIsProcessingPayment(false);
          toast.success('✅ QR ödeme tamamlandı!');
          setShowPaymentModal(false);
          try { await realtimeCartAPI.clearQRPayment(); } catch (e) {}
          try { await realtimeCartAPI.clearAfterPayment(); } catch (e) {}
          setGiftCode('');
          setAppliedGiftCode(null);
          setGiftCodeDiscount(0);
          setLoyaltyCode('');
          setAppliedLoyaltyCode(null);
          setTempLoyaltyCount(0);
          setPendingLoyaltyReward(null);
          await clearCart(false);
          try {
            const orderData = await ordersAPI.getById(orderId);
            navigate(`/receipt/${orderId}`, { state: { paymentType: 'qr', completedAt: new Date().toISOString() } });
          } catch (error) {}
        } else if (['CANCELLED', 'EXPIRED', 'FAILED', 'AUTHORIZATION_FAILED'].includes(status.status)) {
          clearInterval(checkInterval);
          setQrPaymentInterval(null);
          setIsQrPaymentActive(false);
          setQrPaymentData(null);
          setCurrentOrderId(null);
          setIsProcessingPayment(false);
          if (status.status === 'EXPIRED') toast.error('⏰ QR ödeme süresi doldu');
          else if (status.status === 'CANCELLED') toast.error('🚫 QR ödeme iptal edildi');
          else toast.error('❌ QR ödeme başarısız');
          try { await realtimeCartAPI.clearQRPayment(); } catch (e) {}
        }
      } catch (error) {
      }
    }, 3000);
    setQrPaymentInterval(checkInterval);
  };

  const cancelQrPayment = async () => {
    if (!currentOrderId) {
      toast.error('QR ödeme iptal edilemiyor');
      return;
    }
    setIsQrPaymentActive(false);
    setQrPaymentData(null);
    setQrPaymentStatus('');
    const orderIdToCancel = currentOrderId;
    setCurrentOrderId(null);
    setQrPaymentCancelledByUser(true);
    setIsProcessingPayment(false);
    if (qrPaymentInterval) {
      clearInterval(qrPaymentInterval);
      setQrPaymentInterval(null);
    }
    if (user && ['admin', 'garson', 'mutfak'].includes(user.role)) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/csrf-token`, { credentials: 'include' });
        await payconiqAPI.cancelPayment(orderIdToCancel);
        toast.success('🚫 QR ödeme iptal edildi');
      } catch (error: any) {
        toast.success('🚫 QR ödeme iptal edildi (backend bağlantı hatası)');
      }
      try { await realtimeCartAPI.clearQRPayment(); } catch (error) {}
    } else {
      toast.success('🚫 QR ödeme iptal edildi');
    }
  };

  const printReceipt = () => {
    if (!completedOrder) return;
    printStandardReceipt(completedOrder, receiptSettings, formatPrice);
    toast.success('Fiş yazdırılıyor...');
  };

  const finishOrder = async () => {
    try {
      await clearCart(false);
      setQrPaymentCancelledByUser(false);
      setShowReceiptModal(false);
      setCompletedOrder(null);
      setCompletedOrderGiftCode(null);
      setWantsReceipt(false);
      toast.success('İşlem tamamlandı!');
    } catch (error) {
      setShowReceiptModal(false);
      setCompletedOrder(null);
      setCompletedOrderGiftCode(null);
      setWantsReceipt(false);
      toast.error('İşlem tamamlanırken hata oluştu');
    }
  };

  const updateRealtimeCart = async () => {
    const preserveQr = !qrPaymentCancelledByUser;
    updateRealtimeCartWithData(cart, true, preserveQr);
  };

  const updateRealtimeCartWithData = async (cartData: typeof cart, forceEnabled?: boolean, preserveQrPayment = true) => {
    const isEnabled = true;
    if (!(user && ['admin', 'garson', 'mutfak'].includes(user.role))) {
      return;
    }
    const subtotal = cartData.reduce((total, item) => item.notes === '🎁 ÖDÜL' ? total : total + (item.product.price * item.quantity), 0);
    const taxTotal = cartData.reduce((total, item) => {
      if (item.notes === '🎁 ÖDÜL') return total;
      const itemTotal = item.product.price * item.quantity;
      const taxRate = item.product.tax_rate || 0;
      const taxAmount = itemTotal - (itemTotal / (1 + taxRate / 100));
      return total + taxAmount;
    }, 0);
    const finalTotal = subtotal;
    const discountedTotal = Math.max(0, finalTotal - giftCodeDiscount);
    const totals = { subtotal, tax: taxTotal, total: discountedTotal, discount: giftCodeDiscount };
    const payload = {
      items: cartData.map(item => ({
        id: item.product.id,
        product_id: item.product.id,
        product_name: item.product.name,
        quantity: item.quantity,
        unit_price: item.product.price,
        notes: item.notes,
        is_reward: item.notes === '🎁 ÖDÜL'
      })),
      totals,
      enabled: isEnabled,
      coupons: {
        giftCode: appliedGiftCode ? { code: appliedGiftCode.code, discount: appliedGiftCode.discount || 0, type: appliedGiftCode.type } : null,
        loyaltyCode: appliedLoyaltyCode ? {
          code: appliedLoyaltyCode.code,
          points: appliedLoyaltyCode.points,
          reward: appliedLoyaltyCode.reward,
          currentCount: appliedLoyaltyCode.current_count + (tempLoyaltyCount || 0),
          requiredQuantity: appliedLoyaltyCode.required_quantity,
          isCompleted: appliedLoyaltyCode.is_completed || (appliedLoyaltyCode.current_count + (tempLoyaltyCount || 0)) >= appliedLoyaltyCode.required_quantity,
          rewardUsedAt: appliedLoyaltyCode.reward_used_at
        } : null
      },
      preserveQrPayment: preserveQrPayment && !qrPaymentCancelledByUser
    };
    try {
      if (user && ['admin', 'garson', 'mutfak'].includes(user.role)) {
        await realtimeCartAPI.update(payload);
      }
    } catch (error: any) {
    }
  };

  const handleAdminRefreshCartState = async (products: Product[]) => {
    try {
      const response = await realtimeCartAPI.getAdminState();
      if (response.success && response.cart && response.cart.items && response.cart.items.length > 0) {
        await clearBackendCartState();
        toast.success('Admin panel yenilendi - sepet temizlendi, yeni siparişe başlayabilirsiniz');
      }
    } catch (error) {}
  };

  const clearBackendCartState = async () => {
    try {
      setCart([]);
      setAppliedGiftCode(null);
      setGiftCode('');
      setGiftCodeDiscount(0);
      setAppliedLoyaltyCode(null);
      setLoyaltyCode('');
      setTempLoyaltyCount(0);
      setPendingLoyaltyReward(null);
      setIsQrPaymentActive(false);
      setQrPaymentData(null);
      setQrPaymentStatus('');
      setCurrentOrderId(null);
      await updateRealtimeCartWithData([], true, false);
    } catch (error) {
      toast.error('Sepet temizlenirken hata oluştu');
    }
  };

  const loadBackendCartState = async (products: Product[]) => {
    try {
      const response = await realtimeCartAPI.getAdminState();
      if (response.success && response.cart) {
        const backendCart = response.cart;
        if (backendCart.items && backendCart.items.length > 0) {
          const productMap = new Map(products.map((p: Product) => [p.id, p]));
          const frontendCart = backendCart.items.map((item: any, index: number) => {
            const product = productMap.get(item.id);
            if (!product) return null;
            return { id: Date.now() + index, product, quantity: item.quantity, notes: item.notes || '', timestamp: Date.now() };
          }).filter(Boolean);
          if (frontendCart.length > 0) {
            setCart(frontendCart as typeof cart);
          }
        }
        if (backendCart.qrPayment) {
          setQrPaymentData(backendCart.qrPayment);
          setQrPaymentStatus(backendCart.qrPayment.status || 'PENDING');
          setIsQrPaymentActive(true);
          setCurrentOrderId(backendCart.qrPayment.orderId);
        }
        if (backendCart.coupons) {
          if (backendCart.coupons.giftCode) {
            setAppliedGiftCode(backendCart.coupons.giftCode);
            setGiftCodeDiscount(backendCart.coupons.giftCode.discount || 0);
          }
          if (backendCart.coupons.loyaltyCode) {
            setAppliedLoyaltyCode(backendCart.coupons.loyaltyCode);
          }
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const categoriesData = await productsAPI.getCategories();
        setCategories(categoriesData);
        const allProductsData = await productsAPI.getAll();
        const activeProducts = allProductsData.filter((p: Product) => p.is_active);
        setAllProducts(activeProducts);
        setSelectedCategory(null);
        await handleAdminRefreshCartState(activeProducts);
      } catch (error) {
        toast.error('Veriler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    loadData();
    loadReceiptSettings();
  }, []);

  // Shortcut: Ctrl/Cmd + K to focus search
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      if ((isMac ? e.metaKey : e.ctrlKey) && (e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Category scroll fade indicators
  useEffect(() => {
    const el = catScrollRef.current;
    if (!el) return;
    const update = () => {
      setCatCanScrollLeft(el.scrollLeft > 2);
      setCatCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };
    update();
    el.addEventListener('scroll', update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [categories.length]);

  useEffect(() => {
    let filtered = allProducts;
    if (selectedCategory) {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }
    if (searchTerm) {
      filtered = filtered.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    setProducts(filtered);
  }, [selectedCategory, searchTerm, allProducts]);

  useEffect(() => {
    if (cart.length > 0) {
      const preserveQr = !qrPaymentCancelledByUser;
      updateRealtimeCartWithData(cart, true, preserveQr);
    } else if (cart.length === 0 && qrPaymentCancelledByUser) {
      updateRealtimeCartWithData([], true, false);
    }
  }, [appliedGiftCode, giftCodeDiscount, appliedLoyaltyCode, tempLoyaltyCount, cart, qrPaymentCancelledByUser]);

  const loadReceiptSettings = async () => {
    try {
      const settings = await settingsAPI.getReceiptSettings();
      setReceiptSettings({
        receiptTitle: settings.receiptTitle || '',
        receiptSubtitle: settings.receiptSubtitle || '',
        receiptPhone: settings.receiptPhone || '',
        receiptTaxNumber: settings.receiptTaxNumber || '',
        receiptFooter: settings.receiptFooter || '',
        receiptDateLabel: settings.receiptDateLabel || '',
        receiptTimeLabel: settings.receiptTimeLabel || '',
        receiptOrderNumberLabel: settings.receiptOrderNumberLabel || '',
        receiptOrderDetailsLabel: settings.receiptOrderDetailsLabel || '',
        receiptPaymentLabel: settings.receiptPaymentLabel || '',
        receiptPaymentCash: settings.receiptPaymentCash || '',
        receiptPaymentCard: settings.receiptPaymentCard || '',
        receiptSubtotalLabel: settings.receiptSubtotalLabel || '',
        receiptTaxLabel: settings.receiptTaxLabel || '',
        receiptTotalLabel: settings.receiptTotalLabel || '',
        receiptQuantityLabel: settings.receiptQuantityLabel || '',
        receiptShowTaxDetails: settings.receiptShowTaxDetails || false,
        receiptShowPaymentType: settings.receiptShowPaymentType || false,
        receiptShowOrderNumber: settings.receiptShowOrderNumber || false,
        receiptShowDateTime: settings.receiptShowDateTime || false
      });
    } catch (error) {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-cafe-cream dark:from-[#0b0b0e] dark:to-dark-bg transition-colors duration-500">
        <div className="container mx-auto px-4 py-4">
          {/* Topbar skeleton */}
          <div className="bg-white/70 dark:bg-[#141416]/80 backdrop-blur-md rounded-2xl border border-white/60 dark:border-white/10 p-3 mb-4">
            <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="mt-3 flex gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
              ))}
            </div>
          </div>
          {/* Grid skeleton */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1.5">
            {Array.from({ length: 18 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="aspect-square bg-gray-200 dark:bg-gray-700 animate-pulse" />
                <div className="p-2 space-y-2">
                  <div className="h-3 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-cafe-cream dark:from-[#0b0b0e] dark:to-dark-bg transition-colors duration-500">
      <div className="bg-white dark:bg-dark-surface shadow-lg border-b border-gray-200 dark:border-dark-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-cafe-brown dark:text-cafe-light">
                {pageTitle}
              </h1>
              <p className="text-sm text-gray-600 dark:text-dark-text mt-1">
                Hoş geldiniz, {user?.username} ({user?.role})
              </p>
            </div>
            <div className="hidden lg:flex flex-col items-center justify-center flex-1 mx-8">
              <div className="text-2xl text-cafe-brown dark:text-cafe-light font-bold">
                🕐 {getCurrentTimeBelgiumString().split(' ')[1]}
              </div>
              <div className="text-sm text-gray-600 dark:text-dark-text">
                {getCurrentTimeBelgiumString().split(' ')[0]}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button variant="secondary" size="sm" onClick={toggleTheme} title="Tema değiştir">{isDark ? '☀️' : '🌙'}</Button>
              <Button variant="success" size="sm" onClick={() => navigate('/orders')} className="text-xs sm:text-sm">📋 Siparişler</Button>
              <Button variant="info" size="sm" onClick={() => navigate('/tables')} className="text-xs sm:text-sm">🍽️ Masalar</Button>
              {user?.role === 'admin' && (
                <Button variant="warning" size="sm" onClick={() => navigate('/admin')} className="text-xs sm:text-sm">⚙️ Admin</Button>
              )}
              <Button variant="danger" size="sm" onClick={logout} className="text-xs sm:text-sm">🚪 Çıkış</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4">
        {/* Sol: arama + kategoriler + ürünler | Sağ: sepet (aynı satır başlangıcı) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-9">
            <div className="bg-white/80 dark:bg-[#141416]/80 backdrop-blur-md rounded-2xl shadow-lg p-3 border border-white/60 dark:border-white/10 overflow-visible mb-4 sticky top-4 z-10">
              <div className="flex flex-col gap-2">
                <div className="w-full max-w-md">
                  <input
                    type="text"
                    placeholder="Ürün Ara... (Ctrl/Cmd+K)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    ref={searchInputRef}
                    className="w-full px-3 py-2 border border-gray-300/70 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-cafe-brown focus:border-cafe-brown dark:bg-[#0f0f12] dark:text-dark-text text-sm"
                  />
                </div>
                <div className="relative pt-1">
                  {/* fade indicators */}
                  {catCanScrollLeft && (
                    <div className="pointer-events-none absolute left-0 top-0 h-full w-6 bg-gradient-to-r from-white/80 to-transparent dark:from-[#141416] rounded-l-2xl" />
                  )}
                  {catCanScrollRight && (
                    <div className="pointer-events-none absolute right-0 top-0 h-full w-6 bg-gradient-to-l from-white/80 to-transparent dark:from-[#141416] rounded-r-2xl" />
                  )}
                  <div ref={catScrollRef} className="overflow-x-auto no-scrollbar whitespace-nowrap pr-4">
                    <div className="inline-flex items-center gap-2">
                      <button
                    onClick={() => setSelectedCategory(null)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all whitespace-nowrap ${selectedCategory === null ? 'bg-cafe-brown text-white shadow ring-1 ring-white/20 dark:bg-cafe-light dark:text-dark-bg' : 'bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg'}`}
                    aria-current={selectedCategory === null}
                  >
                    Tümü
                  </button>
                      {categories.map((category) => (
                        <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all whitespace-nowrap ${selectedCategory === category.id ? 'bg-cafe-brown text-white shadow ring-1 ring-white/20 dark:bg-cafe-light dark:text-dark-bg' : 'bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg'}`}
                      aria-current={selectedCategory === category.id}
                    >
                      {category.name}
                    </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-4 border border-gray-100 dark:border-dark-border">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1.5 max-h-[600px] overflow-y-auto">
                {products.map((product) => (
                  <div 
                    key={product.id}
                    className="group bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden cursor-pointer hover:shadow-md hover:border-cafe-brown/30 hover:-translate-y-0.5 transition-all duration-200"
                    onClick={() => addToCart(product)}
                    title={`${product.name} - ${formatPrice(product.price)}`}
                  >
                    <div className="relative bg-gray-100 dark:bg-gray-700 aspect-square">
                      {product.image_url ? (
                        <img
                          src={product.image_url.startsWith('http') ? product.image_url : `${process.env.REACT_APP_API_URL || window.location.origin}${product.image_url}`}
                          alt={product.name}
                          className="w-full h-full object-cover rounded-t-lg"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400 dark:text-gray-500"><span class="text-lg">🖼️</span></div>';
                          }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
                          <span className="text-2xl">🍽️</span>
                        </div>
                      )}
                      {product.stock_quantity !== null && product.stock_quantity !== undefined && product.stock_quantity <= 0 && (
                        <div className="absolute top-1 right-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-red-600" title="Stok yok"></span>
                        </div>
                      )}
                      {/* Quick add button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                        className="absolute bottom-1 right-1 w-7 h-7 rounded-full bg-cafe-brown text-white flex items-center justify-center shadow hover:scale-105 transition-transform"
                        title="Hızlı ekle"
                      >
                        +
                      </button>
                    </div>
                    <div className="p-1.5">
                      <h3 className="font-normal text-gray-900 dark:text-dark-text mb-0.5 text-xs truncate">{product.name}</h3>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-cafe-brown dark:text-cafe-light">{formatPrice(product.price)}</span>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); openNoteModal(product); }}
                          className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white p-1 rounded text-[10px] hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors opacity-0 group-hover:opacity-100"
                          title="Not ekle"
                        >
                          📝
                        </button>
                      </div>
                      <div className="pointer-events-none absolute top-1 left-1 rounded-full bg-white/80 dark:bg-black/40 backdrop-blur px-2 py-0.5 text-[10px] font-medium text-cafe-brown dark:text-cafe-light shadow-sm">
                        {formatPrice(product.price)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {products.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">
                    {searchTerm ? 'Arama kriterinize uygun ürün bulunamadı' : 'Bu kategoride ürün bulunmuyor'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    {searchTerm ? 'Farklı arama terimleri deneyin' : 'Başka bir kategori seçin'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white/80 dark:bg-[#141416]/80 backdrop-blur-md rounded-2xl shadow-lg p-5 border border-white/60 dark:border-white/10 sticky top-6">
              <h3 className="text-base font-semibold mb-3 text-gray-700 dark:text-dark-text flex items-center">
                🛒 Sepet <span className="ml-2 text-xs text-gray-500">({cart.length})</span>
              </h3>
              {cart.length === 0 ? (
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">Sepet boş</p>
                  <div className="p-4 bg-gray-50 dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-dark-border">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-dark-text mb-2">🎁 Sadakat Kodu Sorgula</h4>
                    <div className="flex gap-2 w-full">
                      <input type="text" value={loyaltyCode} onChange={(e) => setLoyaltyCode(e.target.value.toUpperCase())} placeholder="A7B2" className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text text-sm focus:ring-2 focus:ring-cafe-brown focus:border-cafe-brown" />
                      <button onClick={validateLoyaltyCode} disabled={isValidatingLoyaltyCode || !loyaltyCode.trim()} className="px-4 py-2 bg-cafe-brown text-white rounded-lg hover:bg-cafe-dark disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap">{isValidatingLoyaltyCode ? '⏳' : '🔍'}</button>
                    </div>
                    {appliedLoyaltyCode && (
                      <div className={`mt-3 p-3 rounded-lg border ${
                        appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
                        appliedLoyaltyCode.is_completed && appliedLoyaltyCode.reward_used_at ? 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800' :
                        'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">🎯 {appliedLoyaltyCode.code}</p>
                            <p className={`text-xs ${appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at ? 'text-green-600 dark:text-green-300' : appliedLoyaltyCode.is_completed && appliedLoyaltyCode.reward_used_at ? 'text-gray-600 dark:text-gray-300' : 'text-red-600 dark:text-red-300'}`}>
                              {appliedLoyaltyCode.current_count}/{appliedLoyaltyCode.required_quantity} tamamlandı
                            </p>
                            {appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at && (
                              <p className="text-xs text-green-600 dark:text-green-400 font-medium">🎉 Ödül hazır! {appliedLoyaltyCode.reward_product_name}</p>
                            )}
                            {appliedLoyaltyCode.is_completed && appliedLoyaltyCode.reward_used_at && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">✅ Ödül kullanıldı</p>
                            )}
                            {!appliedLoyaltyCode.is_completed && (
                              <p className="text-xs text-red-600 dark:text-red-400 font-medium">❌ Henüz tamamlanmadı! Daha fazla alışveriş yapın.</p>
                            )}
                          </div>
                          {appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at && !pendingLoyaltyReward && (
                            <button onClick={applyLoyaltyReward} className="px-3 py-1 bg-green-500 text-white rounded text-xs font-medium hover:bg-green-600 transition-colors">🎁 Ödülü Kullan</button>
                          )}
                          {pendingLoyaltyReward && (
                            <button disabled className="px-3 py-1 bg-gray-400 text-white rounded text-xs font-medium cursor-not-allowed">✅ Ödül Beklemede</button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                    {cart.map((item, index) => (
                      <div key={index} className="flex justify-between items-center border-b border-gray-200 dark:border-dark-border pb-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-800 dark:text-dark-text">{item.product?.name}{item.notes === '🎁 ÖDÜL' && (<span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-full">ÖDÜL</span>)}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{item.notes === '🎁 ÖDÜL' ? (<span className="text-green-600 dark:text-green-400 font-medium">ÖDÜL × {item.quantity}</span>) : (`${formatPrice(item.product.price)} × ${item.quantity}`)}</p>
                        </div>
                        <div className="flex items-center space-x-1.5">
                          <button onClick={() => updateCartItemQuantity(index, item.quantity - 1)} className="w-7 h-7 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors">-</button>
                          <span className="w-7 text-center text-sm font-medium text-gray-800 dark:text-dark-text">{item.quantity}</span>
                          <button onClick={() => updateCartItemQuantity(index, item.quantity + 1)} className="w-7 h-7 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors">+</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-200 dark:border-dark-border pt-4">
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm"><span className="text-gray-600 dark:text-gray-400">Ara Toplam</span><span className="text-gray-700 dark:text-gray-300">{formatPrice(calculateTotal())}</span></div>
                      {calculateTax() > 0 && (<div className="flex justify-between text-sm"><span className="text-gray-600 dark:text-gray-400">KDV</span><span className="text-gray-700 dark:text-gray-300">{formatPrice(calculateTax())}</span></div>)}
                      <div className="flex justify-between items-center border-t border-gray-200 dark:border-dark-border pt-2"><span className="text-base font-semibold text-gray-800 dark:text-dark-text">Genel Toplam</span><span className="text-xl font-bold text-cafe-brown dark:text-cafe-light">{formatPrice(calculateGrandTotal())}</span></div>
                    </div>
                    <div className="space-y-3">
                      {user?.can_process_payments || user?.role === 'admin' ? (
                        <button onClick={startPaymentProcess} className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-2.5 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 font-semibold">💳 Ödeme Yap</button>
                      ) : (
                        <button disabled className="w-full bg-gray-400 text-white py-2.5 rounded-lg cursor-not-allowed font-semibold" title="Tema değiştir">🚫 Ödeme İzni Yok</button>
                      )}
                      <button onClick={async () => await clearCart()} className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-2.5 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-semibold">🗑️ Sepeti Temizle</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Ödeme Modalı */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setIsProcessingPayment(false);
          if (qrPaymentInterval) {
            clearInterval(qrPaymentInterval);
            setQrPaymentInterval(null);
          }
          setLoyaltyCode('');
          setAppliedLoyaltyCode(null);
          setTempLoyaltyCount(0);
          setPendingLoyaltyReward(null);
        }}
        title="Ödeme"
        size="md"
        closeOnOverlayClick={false}
      >
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-gray-800 dark:text-white mb-3">🛒 Sipariş Özeti</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {cart.map((item, index) => (
              <div key={index} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 dark:text-white">
                      {item.product?.name}
                      {item.notes === '🎁 ÖDÜL' && (
                        <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-full">ÖDÜL</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {item.notes === '🎁 ÖDÜL' ? (
                        <span className="text-green-600 dark:text-green-400 font-medium">{item.quantity} x ÖDÜL</span>
                      ) : (
                        `${item.quantity} x ${formatPrice(item.product?.price || 0)}`
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-800 dark:text-white">
                      {item.notes === '🎁 ÖDÜL' ? (
                        <span className="text-green-600 dark:text-green-400">ÖDÜL <span className="line-through text-gray-400 ml-2">{formatPrice(item.quantity * (item.product?.price || 0))}</span></span>
                      ) : (
                        formatPrice(item.quantity * (item.product?.price || 0))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-300 dark:border-gray-600 pt-3 mt-3 space-y-2">
            {(() => {
              const subtotal = cart.reduce((total, item) => item.notes === '🎁 ÖDÜL' ? total : total + ((item.product?.price || 0) * item.quantity), 0);
              const taxTotal = calculateTax();
              let taxInfo = '';
              if (taxTotal > 0) {
                const uniqueTaxRates = new Set(cart.map(item => item.product?.tax_rate || 0).filter(rate => rate > 0));
                if (uniqueTaxRates.size === 1) taxInfo = `KDV (%${Array.from(uniqueTaxRates)[0]})`;
                else if (uniqueTaxRates.size > 1) taxInfo = 'KDV (Farklı oranlar)';
                else taxInfo = 'KDV';
              } else taxInfo = 'KDV yok';
              const finalTotal = subtotal;
              return (
                <>
                  <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300"><span>Ara Toplam:</span><span>{formatPrice(subtotal)}</span></div>
                  {taxTotal > 0 && (<div className="flex justify-between text-sm text-gray-700 dark:text-gray-300"><div><span>Vergi:</span><div className="text-xs text-gray-500 dark:text-gray-400">{taxInfo}</div></div><span>{formatPrice(taxTotal)}</span></div>)}
                  {giftCodeDiscount > 0 && (<div className="flex justify-between text-sm text-green-600 dark:text-green-400"><div><span>🎁 Hediye Kodu İndirimi:</span><div className="text-xs text-gray-500 dark:text-gray-400">{appliedGiftCode?.code}</div></div><span>-{formatPrice(giftCodeDiscount)}</span></div>)}
                  <div className="flex justify-between text-lg font-bold text-gray-800 dark:text-white border-t border-gray-300 dark:border-gray-600 pt-2"><span>TOPLAM:</span><span className="text-cafe-brown dark:text-cafe-light">{formatPrice(Math.max(0, finalTotal - giftCodeDiscount))}</span></div>
                </>
              );
            })()}
          </div>
        </div>
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-3">🎁 Hediye Kodu</h4>
          {!appliedGiftCode ? (
            <div className="flex gap-2">
              <input type="text" value={giftCode} onChange={(e) => setGiftCode(e.target.value.toUpperCase())} placeholder="Hediye kodu girin..." className="flex-1 border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text" />
              <Button variant="primary" size="sm" onClick={validateGiftCode} disabled={!giftCode.trim() || isValidatingCode}>{isValidatingCode ? '⏳' : '✅'} Uygula</Button>
            </div>
          ) : (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-green-700 dark:text-green-300 font-medium">✅ {appliedGiftCode.code} uygulandı</span>
                  <div className="text-sm text-green-600 dark:text-green-400">{formatPrice(giftCodeDiscount)} indirim</div>
                </div>
                <Button variant="danger" size="sm" onClick={() => { setGiftCode(''); setAppliedGiftCode(null); setGiftCodeDiscount(0); }}>❌ Kaldır</Button>
              </div>
            </div>
          )}
        </div>
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-3">🎴 Sadakat Kodu</h4>
          {!appliedLoyaltyCode ? (
            <div className="flex gap-2">
              <input type="text" value={loyaltyCode} onChange={(e) => setLoyaltyCode(e.target.value.toUpperCase())} placeholder="Sadakat kodu girin..." className="flex-1 border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text" />
              <Button variant="primary" size="sm" onClick={validateLoyaltyCode} disabled={!loyaltyCode.trim() || isValidatingLoyaltyCode}>{isValidatingLoyaltyCode ? '⏳' : '✅'} Kontrol Et</Button>
            </div>
          ) : (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">🎴 {appliedLoyaltyCode.code}</span>
                  <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">{appliedLoyaltyCode.is_completed ? (appliedLoyaltyCode.reward_used_at ? '❌ Ödül zaten kullanılmış' : '🎉 Ödül kullanılabilir!') : `📊 İlerleme: ${appliedLoyaltyCode.current_count + tempLoyaltyCount}/${appliedLoyaltyCode.required_quantity}`}</div>
                </div>
                <div className="flex gap-2">
                  {((appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at && !pendingLoyaltyReward) || (tempLoyaltyCount > 0 && (appliedLoyaltyCode.current_count + tempLoyaltyCount) >= appliedLoyaltyCode.required_quantity && !appliedLoyaltyCode.reward_used_at && !pendingLoyaltyReward)) ? (
                    <Button variant="primary" size="sm" onClick={applyLoyaltyReward}>🎁 Ödülü Kullan</Button>
                  ) : pendingLoyaltyReward ? (
                    <Button variant="secondary" size="sm" disabled>✅ Ödül Beklemede</Button>
                  ) : null}
                  <Button variant="danger" size="sm" onClick={removeLoyaltyCode}>❌ Kaldır</Button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-3">Ödeme Türü Seçin</h4>
          <div className="grid grid-cols-3 gap-3">
            <button onClick={() => { setPaymentType('nakit'); setWantsReceipt(false); }} className={`p-4 rounded-lg border-2 transition-all duration-200 ${paymentType === 'nakit' ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'border-gray-300 dark:border-dark-border hover:border-gray-400'}`}><div className="text-2xl mb-2">💵</div><div className="font-semibold text-gray-800 dark:text-dark-text">Nakit</div></button>
            <button onClick={() => { setPaymentType('kart'); setWantsReceipt(false); }} className={`p-4 rounded-lg border-2 transition-all duration-200 ${paymentType === 'kart' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-300 dark:border-dark-border hover:border-gray-400'}`}><div className="text-2xl mb-2">💳</div><div className="font-semibold text-gray-800 dark:text-dark-text">Kart</div></button>
            <button onClick={() => { setPaymentType('qr'); setWantsReceipt(false); }} className={`p-4 rounded-lg border-2 transition-all duration-200 ${paymentType === 'qr' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' : 'border-gray-300 dark:border-dark-border hover:border-gray-400'}`}><div className="text-2xl mb-2">📱</div><div className="font-semibold text-gray-800 dark:text-dark-text">QR</div></button>
          </div>
        </div>
        {paymentType === 'nakit' && (
          <div className="mb-6">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input type="checkbox" checked={wantsReceipt} onChange={(e) => setWantsReceipt(e.target.checked)} className="w-4 h-4 text-cafe-brown bg-gray-100 border-gray-300 rounded focus:ring-cafe-brown focus:ring-2" />
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>🧾 Fiş istedi</span>
            </label>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Müşteri fiş istiyorsa bu kutuyu işaretleyin</p>
          </div>
        )}
        <div className="flex space-x-3">
          <Button variant="secondary" size="lg" fullWidth onClick={async () => {
            if (isQrPaymentActive && currentOrderId) { await cancelQrPayment(); }
            setShowPaymentModal(false);
            setIsQrPaymentActive(false);
            setQrPaymentData(null);
            setQrPaymentStatus('');
            setCurrentOrderId(null);
            if (qrPaymentInterval) { clearInterval(qrPaymentInterval); setQrPaymentInterval(null); }
          }}>İptal</Button>
          {paymentType === 'qr' && !isQrPaymentActive && (
            <Button variant="primary" size="lg" fullWidth onClick={processQuickPayment} disabled={isProcessingPayment}>📱 QR ile Ödeme</Button>
          )}
          {paymentType === 'qr' && isQrPaymentActive && (
            <Button variant="warning" size="lg" fullWidth disabled>⏳ Ödeme Bekleniyor...</Button>
          )}
          {paymentType !== 'qr' && (
            <Button variant="success" size="lg" fullWidth onClick={() => { processQuickPayment(); }} disabled={isProcessingPayment}>{isProcessingPayment ? (
              <div className="flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>İşleniyor...</div>
            ) : ('✅ Ödemeyi Tamamla')}</Button>
          )}
        </div>
      </Modal>

      {/* Not Ekleme Modalı */}
      <Modal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)} title="Ürün Notu" size="sm">
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-800 dark:text-dark-text mb-2">{selectedProduct?.name}</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Bu ürün için özel not ekleyebilirsiniz</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Not:</label>
            <textarea value={productNote} onChange={(e) => setProductNote(e.target.value)} className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-cafe-brown focus:border-transparent resize-none" rows={3} placeholder="Ürün notu ekleyin..." />
          </div>
          <div className="flex space-x-3">
            <Button variant="secondary" size="lg" fullWidth onClick={() => setShowNoteModal(false)}>İptal</Button>
            <Button variant="success" size="lg" fullWidth onClick={addToCartWithNote}>Sepete Ekle</Button>
          </div>
        </div>
      </Modal>

      {/* Fiş Modalı */}
      <Modal isOpen={showReceiptModal && !!completedOrder} onClose={() => setShowReceiptModal(false)} title="Fiş" size="md">
        <div className="text-center space-y-4">
          <div className="text-green-600 dark:text-green-400">
            <div className="text-4xl mb-3">✅</div>
            <h3 className="text-xl font-bold mb-2 text-gray-800 dark:text-gray-100">Ödeme Başarıyla Alındı!</h3>
            <p className="text-gray-600 dark:text-gray-300">
              Sipariş #{completedOrder?.id} - Hızlı Sipariş<br />
              Toplam: {formatPrice(completedOrder?.total_amount || 0)}<br />
              Ödeme Türü: {completedOrder?.payment_type?.toUpperCase()}
              {completedOrderGiftCode && (<><br /><span className="text-green-600 dark:text-green-400 font-medium">🎁 Hediye Kodu Kullanıldı: {completedOrderGiftCode.code}</span><br /><span className="text-green-600 dark:text-green-400 text-sm">İndirim: {formatPrice(completedOrderGiftCode.discount_amount || 0)}</span></>)}
            </p>
          </div>
          <div className="flex space-x-3">
            {(wantsReceipt || completedOrder?.payment_type === 'kart') && (
              <Button variant="primary" size="lg" fullWidth onClick={printReceipt}>🖨️ Fiş Yazdır</Button>
            )}
            <Button variant="success" size="lg" fullWidth={!(wantsReceipt || completedOrder?.payment_type === 'kart')} onClick={finishOrder}>✅ Tamamla</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default QuickOrderDeneme;


